import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getHostedZone,
    listHostedZones,
} from './queries';

import {
    GetHostedZone,
    HostedZone,
    HostedZonesConnection,
    ListHostedZones,
} from '../types';

const client = generateClient();

// FNP-973 — READ-ONLY. No add, no update, no delete: the deployed schema has no
// hosted-zone mutation to call, and writes are FNP-977.
//
// ⚠️ Errors are RE-THROWN, not swallowed into a notification here.
//
// The sibling modules in this app catch, call showErrorNotifications and return
// undefined. That pattern makes a failed fetch indistinguishable from an empty
// result to React Query — `isError` stays false and the table renders its empty
// state, so "the API is down" and "there are no zones" look identical. This
// module lets the rejection reach React Query (§8 of the module checklist:
// errors surface through `isError`, rendered as an Alert) and keeps
// notification concerns out of the service layer.

export const fetchRecords = async (
    parentDomainName: string | undefined,
    limit: number,
    nextToken: string | null,
): Promise<HostedZonesConnection> => {

    const response = await client.graphql<GraphQLQuery<ListHostedZones>>({
        query: listHostedZones,
        variables: {
            parentDomainName,
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listHostedZones?.items ?? [],
        nextToken: response.data?.listHostedZones?.nextToken ?? null,
    };
};

// The detail fetch is NOT optional sugar over the list row: `nameServers` is
// null on every list item and populated only here (see queries.ts).
export const fetch = async (
    id: string,
    parentDomainName?: string,
): Promise<HostedZone | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetHostedZone>>({
        query: getHostedZone,
        variables: {
            id,
            parentDomainName,
        },
    });

    return response.data?.getHostedZone;
};
